// Pixi application at a fixed 640×360 logical resolution, integer-scaled with letterboxing
// (docs/07 §1.3). The canvas renders at 1× and CSS upscales with nearest-neighbour.
import { Application, Container } from 'pixi.js';

export const LOGICAL_W = 640;
export const LOGICAL_H = 360;

export interface GameApp {
  app: Application;
  /** Everything that shakes. */
  world: Container;
  /** Flashes, vignettes, full-screen fx above the world. */
  overlay: Container;
  scale: number;
  onResize(cb: (scale: number) => void): void;
}

/**
 * Integer scale that fits the window. Screens smaller than 640×360 (phones) get a fractional
 * downscale instead of clipping; nearest filtering keeps it crisp enough to play (docs/07 §1.3).
 */
export function computeScale(w: number, h: number): number {
  const fit = Math.min(w / LOGICAL_W, h / LOGICAL_H);
  return fit >= 1 ? Math.floor(fit) : Math.max(0.25, fit);
}

/** Phones and tablets: side panels become drawers (docs/07 §1.3, under 900 css px). */
export function isTouchLayout(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 900 && window.matchMedia?.('(pointer: coarse)').matches;
}

export async function createApp(stageEl: HTMLElement, canvasHost: HTMLElement): Promise<GameApp> {
  const app = new Application();
  await app.init({
    width: LOGICAL_W,
    height: LOGICAL_H,
    background: 0x2e222f,
    antialias: false,
    resolution: 1,
    roundPixels: true,
    autoDensity: false,
    preference: 'webgl',
  });
  const canvas = app.canvas;
  canvas.style.imageRendering = 'pixelated';
  canvas.style.display = 'block';
  canvasHost.appendChild(canvas);

  const world = new Container();
  const overlay = new Container();
  app.stage.addChild(world, overlay);

  const listeners: ((s: number) => void)[] = [];
  const ga: GameApp = {
    app,
    world,
    overlay,
    scale: 1,
    onResize: (cb) => listeners.push(cb),
  };

  const resize = () => {
    const s = computeScale(window.innerWidth, window.innerHeight);
    ga.scale = s;
    const w = LOGICAL_W * s;
    const h = LOGICAL_H * s;
    stageEl.style.width = `${w}px`;
    stageEl.style.height = `${h}px`;
    stageEl.style.left = `${Math.floor((window.innerWidth - w) / 2)}px`;
    stageEl.style.top = `${Math.floor((window.innerHeight - h) / 2)}px`;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    document.documentElement.style.setProperty('--px', String(s));
    document.documentElement.classList.toggle('touch', isTouchLayout());
    document.documentElement.classList.toggle('portrait', window.innerHeight > window.innerWidth && s < 1);
    for (const l of listeners) l(s);
  };
  window.addEventListener('resize', resize);
  resize();
  return ga;
}

/** Convert a DOM client point to logical coordinates. */
export function toLogical(ga: GameApp, clientX: number, clientY: number): { x: number; y: number } {
  const r = ga.app.canvas.getBoundingClientRect();
  return { x: ((clientX - r.left) / r.width) * LOGICAL_W, y: ((clientY - r.top) / r.height) * LOGICAL_H };
}
